import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Link,
  useBeforeUnload,
  useBlocker,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ChevronRight, Truck } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import { AppHeaderContent } from '@/components/AppHeaderContent';
import { ModalOverlay } from '@/components/ModalOverlay';
import { extractApiMessage } from '@/lib/api';
import { fmtFecha, fmtInt } from '@/lib/format';
import { decimalSolicitud, entradaDecimal, formatoDecimal } from '@/lib/liquidacion';
import { Decimal } from '@/lib/offline/decimal';
import { currentBundle } from '@/lib/offline/bundle';
import { useEntityId, useEntityMappings } from '@/lib/offline/operations';
import { useCamadaDetalle, useCerrarCamada, useReabrirCamada } from '@/lib/queries/camadas';
import { useActualizarPesoSalida, useSalidasCamada } from '@/lib/queries/salidas';
import { useLiquidacion } from '@/lib/queries/liquidacion';
import { useAuthStore } from '@/stores/auth';
import { LiquidacionEditor } from './LiquidacionPage';
import type { Salida } from '@/lib/types';
import './CierreCamadaPage.css';

const titles = ['Salidas a matadero', 'Pienso', 'Resumen', 'Liquidación'];
type WeightDraft = { kg: string; date: string };
function kgValue(value: string): string | null {
  const normalized = decimalSolicitud(value);
  return /^\d{1,10}(?:\.\d{1,2})?$/.test(normalized) ? new Decimal(normalized).toString() : null;
}
function sameKg(a: string, b: string | number | null) {
  const value = kgValue(a);
  return b != null && value != null && new Decimal(value).compare(b) === 0;
}

export function CierreCamadaPage() {
  const { camadaId: parameter } = useParams();
  const owner = useAuthStore((s) => s.user?.id ?? null);
  useEntityMappings();
  const farm = currentBundle()?.explotacion_id ?? null;
  const identity = `${owner}:${parameter}`;
  const context = useRef({ identity, farm, revision: 0 });
  if (context.current.identity !== identity) context.current = { identity, farm, revision: 0 };
  else if (farm != null) {
    if (context.current.farm != null && context.current.farm !== farm) context.current.revision++;
    context.current.farm = farm;
  }
  return <ClosureFlow key={`${identity}:${context.current.revision}`} parameter={parameter} />;
}

function ClosureFlow({ parameter }: { parameter: string | undefined }) {
  const id = useEntityId(parameter, 'camadas');
  const user = useAuthStore((s) => s.user),
    owner = user?.id;
  const detailQ = useCamadaDetalle(id),
    exitsQ = useSalidasCamada(id);
  const closeQ = useCerrarCamada(),
    reopenQ = useReabrirCamada(),
    weightQ = useActualizarPesoSalida(id ?? 0);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [closedWrite, setClosedWrite] = useState<boolean | null>(null);
  const [feedDraft, setFeedDraft] = useState<{ known: boolean; kg: string } | null>(null);
  const [motive, setMotive] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const selectedId = useEntityId(selected, 'salidas');
  const mappings = useEntityMappings();
  const mapId = (rowId: number) =>
    mappings.find((m) => m.entity === 'salidas' && m.localId === rowId)?.remoteId ?? rowId;
  const [weights, setWeights] = useState<Record<string, WeightDraft>>({});
  const [confirmed, setConfirmed] = useState<Record<number, Salida>>({});
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);
  const [economicDirty, setEconomicDirty] = useState(false),
    [economicBusy, setEconomicBusy] = useState(false);
  const [reopenPrompt, setReopenPrompt] = useState(false);
  const working = useRef(false),
    mounted = useRef(true),
    errorRef = useRef<HTMLDivElement>(null),
    headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const detail = detailQ.data,
    camada = detail?.camada;
  const closed = closedWrite ?? camada?.cerrada ?? false;
  const liquidationQ = useLiquidacion(closed ? id : null);
  useEffect(() => {
    if (camada?.cerrada === closedWrite) setClosedWrite(null);
  }, [camada?.cerrada, closedWrite]);
  const exits = (exitsQ.data ?? [])
    .filter((s) => !s.anulada)
    .map((s) => {
      const saved = Object.values(confirmed).find((c) => mapId(c.id) === s.id);
      return saved &&
        !(
          s.updated_at &&
          saved.updated_at &&
          Date.parse(s.updated_at) > Date.parse(saved.updated_at)
        )
        ? { ...saved, id: s.id }
        : s;
    });
  const allWeights =
    exits.length > 0 &&
    exits.every(
      (s) =>
        s.kg_confirmados_destino != null && new Decimal(s.kg_confirmados_destino).compare(0) > 0,
    );
  const ready = !!detail && exitsQ.data != null && !detailQ.isError && !exitsQ.isError;
  const canEdit = user?.rol === 'admin' || user?.rol === 'ganadero';
  const maxStep = closed ? 4 : allWeights ? 3 : 1;
  const requested = Number(params.get('paso')) || (closed ? 4 : 1);
  const step = Math.min(maxStep, Math.max(1, Math.min(4, requested)));
  const selectedExit = exits.find((s) => s.id === selectedId);
  const feed = feedDraft ?? {
    known: detail?.kpis.kg_sobrante_pienso != null,
    kg: entradaDecimal(detail?.kpis.kg_sobrante_pienso ?? null),
  };
  const delivered = new Decimal(detail?.kpis.pienso_total_kg ?? 0);
  const leftover = feed.known ? kgValue(feed.kg) : null;
  const feedError = !feed.known
    ? detail?.kpis.kg_sobrante_pienso != null
      ? 'El sobrante registrado se conserva. Indica la cantidad corregida.'
      : null
    : leftover == null
      ? 'Introduce kg con hasta dos decimales, incluido 0.'
      : new Decimal(leftover).compare('99999999.99') > 0
        ? 'El sobrante máximo es 99.999.999,99 kg.'
        : new Decimal(leftover).compare(delivered) > 0
          ? 'El sobrante no puede superar el pienso entregado.'
          : null;
  const consumed = leftover != null && !feedError ? delivered.sub(leftover).toString() : null;
  const units = exits.reduce((sum, s) => sum + s.unidades_cargadas_total, 0);
  const balance = (detail?.kpis.aves_iniciales ?? 0) - (detail?.kpis.bajas_acumuladas ?? 0) - units;
  const totalWeight = exits.reduce(
    (sum, s) => sum.add(s.kg_confirmados_destino ?? 0),
    new Decimal(0),
  );
  const justification = motive ?? camada?.motivo_justificacion_descuadre ?? '';
  const weightDirty = Object.entries(weights).some(([rawId, draft]) => {
    const row = exits.find((s) => s.id === mapId(Number(rawId)));
    return (
      !row ||
      !sameKg(draft.kg, row.kg_confirmados_destino) ||
      draft.date !== (row.fecha_recepcion_peso ?? '')
    );
  });
  const feedDirty =
    !closed &&
    feedDraft != null &&
    (feed.known !== (detail?.kpis.kg_sobrante_pienso != null) ||
      (feed.known && !sameKg(feed.kg, detail?.kpis.kg_sobrante_pienso ?? null)));
  const dirty =
    weightDirty ||
    feedDirty ||
    (!closed &&
      motive != null &&
      justification !== (camada?.motivo_justificacion_descuadre ?? '')) ||
    economicDirty;
  const dirtyRef = useRef(dirty),
    busyRef = useRef(busy || economicBusy);
  dirtyRef.current = dirty;
  busyRef.current = busy || economicBusy;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      busyRef.current || (dirtyRef.current && currentLocation.pathname !== nextLocation.pathname),
  );
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useBeforeUnload((event) => {
    if (dirtyRef.current || busyRef.current) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  useEffect(() => {
    headingRef.current?.focus();
  }, [step, selected]);
  const finish = useCallback(() => {
    if (!mounted.current || useAuthStore.getState().user?.id !== owner) return;
    if (weightDirty) {
      navigate(`/camadas?camada=${id}`);
      return;
    }
    dirtyRef.current = false;
    busyRef.current = false;
    if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
    navigate(`/camadas?camada=${id}`, { replace: true });
  }, [id, navigate, owner, weightDirty]);
  function go(next: number) {
    if (
      !mounted.current ||
      useAuthStore.getState().user?.id !== owner ||
      busyRef.current ||
      working.current
    )
      return;
    setSelected(null);
    setError(null);
    setParams({ paso: String(next) });
  }
  async function run(action: () => Promise<void>) {
    if (working.current || !ready || !canEdit) return;
    working.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      if (mounted.current && useAuthStore.getState().user?.id === owner)
        setError(extractApiMessage(err));
    } finally {
      working.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const current = () => mounted.current && useAuthStore.getState().user?.id === owner;
  function openWeight(row: Salida) {
    const key =
      Object.keys(weights).find((rawId) => mapId(Number(rawId)) === row.id) ?? String(row.id);
    setSelected(key);
    setError(null);
    setWeights((previous) =>
      previous[key]
        ? previous
        : {
            ...previous,
            [key]: {
              kg: entradaDecimal(row.kg_confirmados_destino),
              date: row.fecha_recepcion_peso ?? (closed ? '' : format(new Date(), 'yyyy-MM-dd')),
            },
          },
    );
  }
  const weightDraft = selected ? weights[selected] : null;
  const weightKg = weightDraft ? kgValue(weightDraft.kg) : null;
  const weightError = !weightDraft
    ? null
    : weightKg == null || new Decimal(weightKg).compare(0) <= 0
      ? 'Introduce un peso mayor que cero, con hasta dos decimales.'
      : !/^\d{4}-\d{2}-\d{2}$/.test(weightDraft.date) || !isValid(parseISO(weightDraft.date))
        ? 'Introduce una fecha de recepción válida.'
        : selectedExit && weightDraft.date < selectedExit.fecha_carga
          ? 'La recepción del peso no puede ser anterior a la carga.'
          : null;
  const weightChanged =
    !!weightDraft &&
    !!selectedExit &&
    (!sameKg(weightDraft.kg, selectedExit.kg_confirmados_destino) ||
      weightDraft.date !== selectedExit.fecha_recepcion_peso);
  const saveWeight = () =>
    run(async () => {
      if (
        closed ||
        !selectedExit ||
        !weightDraft ||
        weightError ||
        weightKg == null ||
        !weightChanged
      )
        return;
      const result = await weightQ.mutateAsync({
        salidaId: selectedExit.id,
        kg_confirmados_destino: weightKg,
        fecha_recepcion_peso: weightDraft.date,
      });
      if (!current()) return;
      setConfirmed((previous) => ({ ...previous, [result.id]: result }));
      setWeights((previous) => {
        const next = { ...previous };
        delete next[selected!];
        return next;
      });
      setSelected(null);
    });
  const saveClose = () =>
    run(async () => {
      if (
        closed ||
        !allWeights ||
        weightDirty ||
        feedError ||
        balance < 0 ||
        (balance > 0 && !justification.trim())
      )
        return;
      const result = await closeQ.mutateAsync({
        id: id!,
        kg_sobrante_pienso: feed.known ? leftover : null,
        motivo_justificacion_descuadre: justification.trim() || null,
      });
      if (!current()) return;
      setClosedWrite(result.cerrada);
      setFeedDraft(null);
      setMotive(null);
      busyRef.current = false;
      setParams({ paso: '4' });
    });
  const reopen = () =>
    run(async () => {
      if (!closed) return;
      const retainedFeed = feed;
      const result = await reopenQ.mutateAsync({ id: id! });
      if (!current()) return;
      setClosedWrite(result.cerrada);
      setEconomicDirty(false);
      setReopenPrompt(false);
      setFeedDraft(retainedFeed);
      busyRef.current = false;
      setParams({ paso: '1' });
    });
  return (
    <main
      className={`cierre-page ${step === 4 ? 'cierre-economic-page' : ''} mx-auto max-w-md px-4`}
    >
      <header className="ui-global-header" data-testid="app-header">
        <AppHeaderContent backTo={`/camadas?camada=${id}`} backLabel="Camadas" />
      </header>
      <h1 className="ui-page-title" ref={headingRef} tabIndex={-1}>
        {selected && selectedExit
          ? `Peso · ${selectedExit.numero_camion || selectedExit.numero_albaran || 'Camión'}`
          : titles[step - 1]}
      </h1>
      {camada && (
        <div className="cierre-heading">
          <p>
            <strong>{camada.codigo_lote}</strong>
            <span>{camada.sexo === 'machos' ? 'Machos' : 'Hembras'}</span>
          </p>
          <nav aria-label="Pasos del cierre">
            {titles.map((title, index) => (
              <button
                key={title}
                type="button"
                aria-label={`Paso ${index + 1}: ${title}`}
                aria-current={step === index + 1 ? 'step' : undefined}
                disabled={busy || economicBusy || index + 1 >= step}
                onClick={() => go(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </nav>
        </div>
      )}
      {error && (
        <div className="parte-error" role="alert" ref={errorRef} tabIndex={-1}>
          {error}
        </div>
      )}
      {!canEdit ? (
        <p role="alert">Solo el administrador o ganadero puede gestionar el cierre.</p>
      ) : !ready ? (
        <p role={detailQ.isError || exitsQ.isError ? 'alert' : 'status'}>
          {detailQ.isError || exitsQ.isError
            ? 'No se puede cargar el cierre. Conecta y vuelve a intentarlo.'
            : 'Cargando cierre…'}
        </p>
      ) : (
        <>
          {step === 1 && !selected && (
            <>
              <div className="cierre-banner">
                <span>Pesos confirmados</span>
                <strong>
                  {exits.filter((s) => s.kg_confirmados_destino != null).length} de {exits.length}
                </strong>
              </div>
              <ul className="cierre-trucks">
                {exits.map((row) => (
                  <li key={row.id}>
                    <button type="button" onClick={() => openWeight(row)} disabled={busy}>
                      <Truck size={21} aria-hidden />
                      <span>
                        <strong>
                          {row.numero_camion || `Camión · ${row.numero_albaran || row.id}`}
                        </strong>
                        <small>
                          {fmtFecha(row.fecha_carga)} · {row.numero_albaran || 'Sin albarán'} ·{' '}
                          {fmtInt(row.unidades_cargadas_total)} aves
                        </small>
                        <span className="cierre-truck-weight">
                          {row.kg_confirmados_destino == null
                            ? 'Añadir peso'
                            : `${formatoDecimal(row.kg_confirmados_destino, 0)} kg`}
                        </span>
                        {Object.entries(weights).some(
                          ([rawId, draft]) =>
                            mapId(Number(rawId)) === row.id &&
                            (!sameKg(draft.kg, row.kg_confirmados_destino) ||
                              draft.date !== (row.fecha_recepcion_peso ?? '')),
                        ) && <small>Peso editado sin guardar</small>}
                      </span>
                      <ChevronRight size={18} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
              <p className="cierre-help">
                {weightDirty
                  ? 'Hay pesos editados sin guardar. Abre el camión para guardarlos o descartarlos.'
                  : !exits.length
                    ? 'Registra las cargas antes de cerrar la camada.'
                    : allWeights
                      ? 'Todos los pesos están completos. Puedes continuar con el pienso.'
                      : 'Completa el peso de cada camión antes de continuar.'}
              </p>
              <Link className="cierre-text-link" to={`/camadas/${id}/salidas`}>
                Gestionar salidas
              </Link>
            </>
          )}
          {step === 1 &&
            selected &&
            (selectedExit && weightDraft ? (
              <form
                id="cierre-weight"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void saveWeight();
                }}
              >
                <fieldset disabled={busy || closed} className="cierre-weight-form">
                  <p className="cierre-help">
                    {selectedExit.numero_albaran || 'Sin albarán'} ·{' '}
                    {fmtInt(selectedExit.unidades_cargadas_total)} aves
                  </p>
                  {closed && (
                    <p className="cierre-help">
                      Cierre guardado. Para corregir el peso, vuelve al resumen y reabre la camada.
                    </p>
                  )}
                  <label htmlFor="weight-kg">Peso total en destino</label>
                  <div className="cierre-input-unit">
                    <input
                      id="weight-kg"
                      type="text"
                      inputMode="decimal"
                      value={weightDraft.kg}
                      aria-describedby="weight-help"
                      aria-invalid={!!weightError}
                      onChange={(e) =>
                        setWeights((previous) => ({
                          ...previous,
                          [selected]: { ...weightDraft, kg: e.target.value },
                        }))
                      }
                    />
                    <span>kg</span>
                  </div>
                  <label htmlFor="weight-date">Fecha de recepción del peso</label>
                  <input
                    id="weight-date"
                    type="date"
                    min={selectedExit.fecha_carga}
                    value={weightDraft.date}
                    onChange={(e) =>
                      setWeights((previous) => ({
                        ...previous,
                        [selected]: { ...weightDraft, date: e.target.value },
                      }))
                    }
                  />
                  <p id="weight-help" className="cierre-help">
                    {weightError ||
                      (weightKg && selectedExit.unidades_cargadas_total > 0
                        ? `Peso medio · ${formatoDecimal(new Decimal(weightKg).div(selectedExit.unidades_cargadas_total).toString(), 2)} kg/ave`
                        : 'Peso total de este camión, sin redondear el dato guardado.')}
                  </p>
                  {!closed && weightChanged && (
                    <button
                      type="button"
                      className="ui-secondary"
                      onClick={() => {
                        setWeights((previous) => {
                          const next = { ...previous };
                          delete next[selected];
                          return next;
                        });
                        setSelected(null);
                      }}
                    >
                      Descartar cambios del peso
                    </button>
                  )}
                </fieldset>
              </form>
            ) : (
              <p role="alert">
                Esta salida ya no está disponible. Vuelve al listado para revisarla.
              </p>
            ))}
          {step === 2 && (
            <fieldset disabled={busy || closed} className="cierre-feed">
              <div className="cierre-banner">
                <span>Pienso entregado</span>
                <strong>{formatoDecimal(delivered.toString(), 0)} kg</strong>
              </div>
              <legend className="sr-only">Sobrante de pienso</legend>
              <h2>Sobrante al cierre</h2>
              <div className="cierre-feed-options">
                <label>
                  <input
                    type="radio"
                    name="feed-known"
                    checked={feed.known}
                    onChange={() => setFeedDraft({ ...feed, known: true })}
                  />
                  Indicar cantidad
                </label>
                <label>
                  <input
                    type="radio"
                    name="feed-known"
                    checked={!feed.known}
                    disabled={detail.kpis.kg_sobrante_pienso != null}
                    onChange={() => setFeedDraft({ ...feed, known: false })}
                  />
                  Aún no lo sé
                </label>
              </div>
              {feed.known && (
                <>
                  <label htmlFor="feed-leftover">Sobrante de pienso</label>
                  <div className="cierre-input-unit">
                    <input
                      id="feed-leftover"
                      type="text"
                      inputMode="decimal"
                      value={feed.kg}
                      aria-invalid={!!feedError}
                      aria-describedby="feed-help"
                      onChange={(e) => setFeedDraft({ ...feed, kg: e.target.value })}
                    />
                    <span>kg</span>
                  </div>
                </>
              )}
              <p className="cierre-help" id="feed-help">
                {closed
                  ? 'Datos del cierre guardado. Para corregirlos, vuelve al resumen y reabre la camada.'
                  : feedError ||
                    (feed.known
                      ? 'Introduce 0 si no queda pienso.'
                      : 'Puedes cerrar ahora y completar el consumo económico después.')}
              </p>
              {!closed && detail.kpis.kg_sobrante_pienso != null && (
                <p className="cierre-help">
                  El sobrante registrado se conserva al reabrir. Puedes corregir la cantidad.
                </p>
              )}
              <div className="cierre-banner">
                <span>Pienso consumido</span>
                <strong>
                  {consumed == null ? 'Pendiente' : `${formatoDecimal(consumed, 0)} kg`}
                </strong>
              </div>
            </fieldset>
          )}
          {step === 3 && (
            <div className="cierre-summary">
              {closed && <p className="cierre-help">Cierre guardado</p>}
              <section>
                <h2>Balance de animales</h2>
                <dl className="cierre-balance">
                  {[
                    ['Entradas', detail.kpis.aves_iniciales],
                    ['Salidas', units],
                    ['Bajas', detail.kpis.bajas_acumuladas],
                    ['Saldo', balance],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{fmtInt(Number(value))}</dd>
                    </div>
                  ))}
                </dl>
              </section>
              <section>
                <div className="cierre-section-heading">
                  <h2>Salidas a matadero</h2>
                  <button type="button" onClick={() => go(1)}>
                    Revisar
                  </button>
                </div>
                <dl>
                  <div>
                    <dt>Peso total</dt>
                    <dd>{formatoDecimal(totalWeight.toString(), 0)} kg</dd>
                  </div>
                  <div>
                    <dt>Camiones confirmados</dt>
                    <dd>{exits.length}</dd>
                  </div>
                </dl>
              </section>
              <section>
                <div className="cierre-section-heading">
                  <h2>Pienso</h2>
                  <button type="button" onClick={() => go(2)}>
                    Revisar
                  </button>
                </div>
                <dl>
                  {[
                    ['Entregado', delivered.toString()],
                    ['Sobrante', feed.known ? leftover : null],
                    ['Consumido', consumed],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value == null ? 'Pendiente' : `${formatoDecimal(value, 0)} kg`}</dd>
                    </div>
                  ))}
                </dl>
              </section>
              {balance < 0 && (
                <p role="alert">
                  El saldo es negativo. Revisa entradas, bajas y salidas antes de cerrar.
                </p>
              )}
              {balance > 0 && (
                <div>
                  <label htmlFor="closure-motive">Justificación del saldo</label>
                  <textarea
                    id="closure-motive"
                    value={justification}
                    disabled={closed || busy}
                    onChange={(e) => setMotive(e.target.value)}
                  />
                  <p className="cierre-help">
                    Quedan {fmtInt(balance)} aves. Explica la diferencia para cerrar.
                  </p>
                </div>
              )}
              {closed && (
                <button
                  className="ui-secondary w-full"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (economicDirty) setReopenPrompt(true);
                    else void reopen();
                  }}
                >
                  Reabrir y corregir
                </button>
              )}
            </div>
          )}
          {closed && liquidationQ.data?.cerrada && (
            <div className="cierre-economic-editor" hidden={step !== 4}>
              <LiquidacionEditor
                id={id!}
                preparacion={liquidationQ.data}
                refrescar={async () => {
                  const response = await liquidationQ.refetch();
                  if (response.error || !response.data)
                    throw response.error ?? new Error('No se pudo actualizar la liquidación.');
                  return response.data;
                }}
                wizard={{
                  onBack: () => go(3),
                  onFinish: finish,
                  onDefer: () => navigate(`/camadas?camada=${id}`),
                  onDirtyChange: setEconomicDirty,
                  onBusyChange: setEconomicBusy,
                }}
              />
            </div>
          )}
          {step === 4 && !liquidationQ.data?.cerrada && (
            <>
              <p role={liquidationQ.isError ? 'alert' : 'status'}>
                {liquidationQ.isError
                  ? 'Cierre guardado. No se pudo cargar la liquidación.'
                  : 'Cierre guardado. Cargando liquidación…'}
              </p>
              <button type="button" className="ui-secondary w-full" onClick={() => go(3)}>
                Atrás
              </button>
              <button type="button" className="ui-primary mt-3 w-full" onClick={finish}>
                Liquidar más tarde
              </button>
            </>
          )}
          {step !== 4 && (
            <footer className="cierre-actions">
              <button
                type="button"
                className="ui-secondary"
                disabled={busy}
                onClick={() => {
                  if (selected) {
                    setSelected(null);
                    setError(null);
                  } else if (step > 1) go(step - 1);
                  else navigate(`/camadas?camada=${id}`);
                }}
              >
                {selected || step > 1 ? 'Atrás' : 'Salir'}
              </button>
              <button
                type="button"
                className="ui-primary"
                disabled={
                  busy ||
                  (selected
                    ? closed || !selectedExit || !!weightError || !weightChanged
                    : step === 1
                      ? !allWeights || weightDirty
                      : step === 2
                        ? !!feedError
                        : !closed &&
                          (weightDirty ||
                            !!feedError ||
                            balance < 0 ||
                            (balance > 0 && !justification.trim())))
                }
                onClick={() => {
                  if (selected) void saveWeight();
                  else if (step === 3 && !closed) void saveClose();
                  else go(step + 1);
                }}
              >
                {busy
                  ? 'Guardando…'
                  : selected
                    ? 'Guardar peso'
                    : step === 3 && !closed
                      ? 'Confirmar cierre'
                      : 'Continuar'}
                <ChevronRight size={18} aria-hidden />
              </button>
            </footer>
          )}
        </>
      )}
      {reopenPrompt && (
        <ModalOverlay
          ariaLabel="Ajustes económicos sin guardar"
          placement="bottom"
          className="max-w-md"
          onBackdrop={() => setReopenPrompt(false)}
        >
          <h2 className="ui-page-title">Ajustes sin guardar</h2>
          <p className="py-3">
            Reabrir descartará los ajustes económicos de este borrador. La copia económica guardada
            se conserva.
          </p>
          <button
            type="button"
            className="ui-secondary w-full"
            onClick={() => setReopenPrompt(false)}
          >
            Seguir revisando
          </button>
          <button
            type="button"
            disabled={busy}
            className="ui-primary mt-2 w-full"
            onClick={() => void reopen()}
          >
            Reabrir y descartar ajustes
          </button>
        </ModalOverlay>
      )}
      {blocker.state === 'blocked' && (
        <ModalOverlay
          ariaLabel="Cambios sin guardar"
          placement="bottom"
          className="max-w-md"
          onBackdrop={() => blocker.reset()}
        >
          <h2 className="ui-page-title">
            {busy || economicBusy ? 'Guardando…' : 'Cambios sin guardar'}
          </h2>
          <p className="py-3">
            {busy || economicBusy
              ? 'Espera a que termine el guardado antes de salir.'
              : 'Puedes seguir editando o salir sin guardar este borrador.'}
          </p>
          <button className="ui-primary w-full" onClick={() => blocker.reset()}>
            Seguir editando
          </button>
          {!busy && !economicBusy && (
            <button
              className="ui-secondary mt-2 w-full"
              onClick={() => {
                dirtyRef.current = false;
                blocker.proceed();
              }}
            >
              Salir sin guardar
            </button>
          )}
        </ModalOverlay>
      )}
    </main>
  );
}
